import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Newspaper, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Placeholder image for articles without thumbnails - professional gradient
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
  <defs>
    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#1e3a5f;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#0f172a;stop-opacity:1" />
    </linearGradient>
  </defs>
  <rect fill="url(#grad)" width="400" height="300"/>
  <text fill="#60a5fa" font-family="Arial,sans-serif" font-size="14" font-weight="bold" text-anchor="middle" x="200" y="145">INDUSTRY NEWS</text>
  <text fill="#94a3b8" font-family="Arial,sans-serif" font-size="11" text-anchor="middle" x="200" y="165">Building Services Engineering</text>
</svg>
`);

// Strip HTML tags from excerpt
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

// News Card Component
const NewsCard = ({ article }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  
  return (
    <Card 
      className="group h-full flex flex-col overflow-hidden hover:shadow-2xl transition-all duration-500 border border-gray-200 bg-white rounded-2xl transform hover:-translate-y-1"
      data-testid={`news-card-${article.id}`}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-slate-900 overflow-hidden relative">
        <img
          src={article.thumbnail || PLACEHOLDER_IMAGE}
          alt={article.title}
          loading="lazy"
          className={`w-full h-full object-cover group-hover:scale-110 transition-all duration-700 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImgLoaded(true)}
          onError={(e) => { e.target.src = PLACEHOLDER_IMAGE; setImgLoaded(true); }}
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {/* Category badge */}
        <span className="absolute top-3 left-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-full capitalize shadow-lg">
          {article.category || 'News'}
        </span>
      </div>
      
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Source and date */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <span className="font-medium text-blue-600">{article.sourceName}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(article.publishDate)}
          </span>
        </div>
        
        {/* Title */}
        <h3 className="font-semibold text-gray-900 text-base mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {article.titleOverride || article.title}
        </h3>
        
        {/* Excerpt */}
        <p className="text-sm text-gray-600 line-clamp-2 mb-4 flex-1">
          {stripHtml(article.excerptOverride || article.excerpt)}
        </p>
        
        {/* Read more link */}
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 group/link"
        >
          Read more
          <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5" />
        </a>
      </CardContent>
    </Card>
  );
};

// Main Industry News Section Component
export const IndustryNewsSection = ({ maxItems = 3, showViewAll = true }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/api/news?limit=${maxItems}`);
        const data = await response.json();
        
        if (data.success) {
          setArticles(data.articles || []);
        } else {
          setError('Failed to load news');
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Unable to load news at this time');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [maxItems]);

  // Don't render if no articles and not loading
  if (!loading && articles.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden" data-testid="industry-news-section" style={{ contain: 'paint' }}>
      {/* Decorative background element - using absolute positioning instead of transforms */}
      <div className="absolute inset-0 opacity-5 overflow-hidden pointer-events-none">
        <div className="absolute -top-[128px] -left-[128px] w-64 h-64 bg-blue-600 rounded-full blur-3xl" />
        <div className="absolute -bottom-[192px] -right-[192px] w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header - Premium Design */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/10 to-blue-500/10 rounded-full border border-blue-200/50">
                <Newspaper className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-600 uppercase tracking-widest">
                  Stay Informed
                </span>
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 tracking-tight">
              Industry News & <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-500">Insights</span>
            </h2>
            <p className="text-gray-500 max-w-2xl text-lg">
              The latest updates from building services engineering, MEP, sustainability, and UK construction industry.
            </p>
          </div>
          
          {showViewAll && (
            <a
              href="/news"
              className="hidden md:inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 mt-4 md:mt-0 group"
            >
              View all news
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="aspect-video bg-gray-200" />
                <CardContent className="p-4">
                  <div className="h-3 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="h-5 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-8 text-gray-500">
            <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{error}</p>
          </div>
        )}

        {/* News Grid */}
        {!loading && !error && articles.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* Mobile View All Link */}
        {showViewAll && !loading && articles.length > 0 && (
          <div className="mt-8 text-center md:hidden">
            <a
              href="/news"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              View all news
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-xs text-gray-400 text-center mt-8">
          External industry news and updates. Articles link to their original sources.
        </p>
      </div>
    </section>
  );
};

export default IndustryNewsSection;
